import {
  SearchSport,
  CreateSport,
  DeleteSport,
  UpdateSportName,
  Sport,
  CreateSportInput,
  UpdateSportNameInput,
  GetSport,
  UpdateSportState
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Sports/PathItems";
import { SearchInput } from "../../PetKey/Main/Model/1_0_Initial/Core/PathItems";
import { Published } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { TranslationAppender } from "./translationMocks";
import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";

export function mockSportsInterceptors(builder: TransportAbstractionBuilder, appender: TranslationAppender) {
  const dbSports: Sport[] = [];
  const dbSportsPublished: Sport[] = [];

  const getSport = (ctx: string | TransportContext): Result<Sport> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('sportId');
    const sport = dbSports.find(s => s.id === id);
    if (!sport) {
      return Result.notFound('Backoffice.Sports.NotFound', `Sport with id ${id} not found`);
    }
    return Result.ok(sport);
  };

  const saveSport = (result: Result<Sport>, ctx: TransportContext): Result<Sport> => {
    if (!result.success) {
      return result;
    }
    const sport = result.value;
    const existingIndex = dbSports.findIndex(s => s.id === sport.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbSportsPublished.findIndex(s => s.id === sport.id);
      dbSports[existingIndex] = applyStateTransition(dbSportsPublished, existingPublishedIndex, result.meta, sport, sport.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
    } else {
      sport.translationKey = appender('sports', sport.name);
      dbSports.push(sport);
    }
    return Result.ok(sport);
  };

  return builder
    .intercept(new GetSport().handle(async (input, ctx) => {
      return getSport(ctx);
    }))
    .intercept(new SearchSport().handle(async (input: SearchInput, ctx) => {
      const filtered = dbSports
        .slice(input.offset, input.offset + input.limit)
        .filter(s =>
          s.name.toLowerCase().includes(input.query.toLowerCase()) ||
          (s.translationKey && s.translationKey.toLowerCase().includes(input.query.toLowerCase()))
        );
      return Result.ok(filtered);
    }))
    .intercept(new CreateSport().handle(async (input: CreateSportInput, ctx) => {
      const newSport: Sport = {
        id: input.sportId ?? generateUUID(),
        name: input.name,
        translationKey: '',
        state: { state: 'draft' } as Published
      };
      return saveSport(Result.ok(newSport), ctx);
    }))
    .intercept(new DeleteSport().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('sportId');
      const idx = dbSports.findIndex(s => s.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Sports.NotFound', `Sport with id ${id} not found`);
      }
      dbSports.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateSportName().handle(async (input: UpdateSportNameInput, ctx) => {
      return saveSport(getSport(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateSportState().handle(async (input, ctx) => {
      return saveSport(getSport(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
}
